// https://dev.to/keento0809/what-is-user-defined-type-guard-in-typescript-423i

import {AuthError} from "firebase/auth";

/**
 * Type guard to check if an error is an AuthError.
 * This function checks if the provided error object has the properties `code` and `message`,
 * which are typical for Firebase Auth errors.
 * @param error - The error to check, which can be a string, an Error object, or an AuthError.
 * @returns True if the error is an AuthError, false otherwise.
 */
export const isAuthError = (error: unknown): error is AuthError => {
  return (
    !!error &&
    typeof error === "object" &&
    "code" in error &&
    "message" in error
  );
};

/**
 * Type guard for any error carrying a string `code`, such as the FirebaseError
 * thrown by the Messaging SDK. Narrower than `isAuthError` in intent and wider
 * in scope: it makes no claim about the error being an auth error.
 * @param error - The value caught in a catch block.
 * @returns True if the value is an object with a string `code` property.
 */
export const isErrorWithCode = (
  error: unknown,
): error is {code: string} & Record<string, unknown> => {
  return (
    !!error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as {code: unknown}).code === "string"
  );
};
